import { For, Show, createMemo, createSignal, onMount, onCleanup } from "solid-js";
import type { Component } from "solid-js";
import { useQueryClient } from "@tanstack/solid-query";
import { queryKeys } from "@core/query";
import { useShellStore } from "@ui/state/shell";
import {
  dismissAllNotifications,
  dismissNotification,
  dismissNotificationGroup,
  dismissNotificationType,
  NOTIFICATION_TYPES,
  useNotificationsQuery,
} from "../service";
import {
  type NotificationItem,
  type NotificationType,
} from "../service";
import {
  aggregateNotifications,
  getAggregatedTitle,
  getAggregatedMessage,
  type AggregatedNotification
} from "../aggregation";
import { TableDate } from "@ui/components/TableDate";

const typeLabels: Record<NotificationType, string> = {
  bookings: "Bookings",
  robots: "Robots",
  users: "Users",
  chats: "Chats",
};

const typeIcons: Record<NotificationType, string> = {
  bookings: "fa-solid fa-calendar-check",
  robots: "fa-solid fa-robot",
  users: "fa-solid fa-user-group",
  chats: "fa-solid fa-comments",
};

// formatTimestamp removed - using TableDate component instead

const getMetadataValue = (metadata: Record<string, unknown>, key: string): string | null => {
  const value = metadata[key];
  return typeof value === "string" && value.trim().length > 0 ? value : null;
};

export const NotificationsView: Component = () => {
  const queryClient = useQueryClient();
  const notificationsQuery = useNotificationsQuery();
  const {
    actions: { selectBooking, selectChat, selectRobot, selectUser },
  } = useShellStore();
  const [isClearing, setIsClearing] = createSignal(false);
  const [activeTab, setActiveTab] = createSignal<NotificationType>("bookings");
  const [expandedGroups, setExpandedGroups] = createSignal<Set<string>>(new Set());


  const groups = createMemo(() => notificationsQuery.data ?? NOTIFICATION_TYPES.map((type) => ({ type, items: [] })));
  const hasAnyNotifications = createMemo(() => groups().some((group) => group.items.length > 0));

  const activeGroup = createMemo(() => groups().find((g) => g.type === activeTab()) || { type: activeTab(), items: [] });

  // Aggregate notifications for the active tab
  const aggregatedNotifications = createMemo(() => {
    const group = activeGroup();
    return aggregateNotifications(group.items);
  });

  const getTabCount = (type: NotificationType) => {
    const group = groups().find((g) => g.type === type);
    return group?.items.length || 0;
  };

  const invalidateCaches = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.notifications.root() });
    await queryClient.invalidateQueries({ queryKey: queryKeys.stats.dashboard() });
  };

  const handleDismissNotification = async (type: NotificationType, notificationId: string) => {
    setIsClearing(true);
    try {
      await dismissNotification(type, notificationId);
      await invalidateCaches();
    } finally {
      setIsClearing(false);
    }
  };

  const handleDismissGroup = async (agg: AggregatedNotification) => {
    setIsClearing(true);
    try {
      await dismissNotificationGroup(agg.notifications);
      await invalidateCaches();
    } finally {
      setIsClearing(false);
    }
  };

  const handleDismissType = async () => {
    const type = activeTab();
    setIsClearing(true);
    try {
      await dismissNotificationType(type);
      await invalidateCaches();
    } finally {
      setIsClearing(false);
    }
  };

  const handleDismissAll = async () => {
    setIsClearing(true);
    try {
      await dismissAllNotifications();
      await invalidateCaches();
    } finally {
      setIsClearing(false);
    }
  };

  const toggleGroupExpansion = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const isGroupExpanded = (groupKey: string) => {
    return expandedGroups().has(groupKey);
  };

  const handleOpen = (item: NotificationItem) => {
    const link = item.link;
    if (!link) {
      return;
    }

    // Open detail panel without changing navigation
    switch (link.kind) {
      case "booking": {
        const userId = getMetadataValue(item.metadata, "userId");
        selectBooking(link.id, userId);
        break;
      }
      case "chat": {
        const userId = getMetadataValue(item.metadata, "userId");
        selectChat(link.id, userId);
        break;
      }
      case "robot": {
        selectRobot(link.id);
        break;
      }
      case "user": {
        selectUser(link.id);
        break;
      }
      default:
        break;
    }
  };

  return (
    <section class="chat-list-view">
      <header class="chat-list-header">
        <div>
          <h1 class="main-placeholder-title">Notifications</h1>
          <p>Stay on top of new bookings, robot activity, user signups, and HappyChat messages.</p>
        </div>
        <div class="chat-list-actions" style="display: flex; gap: 0.75rem;">
          <button
            type="button"
            class="button-primary refresh-button"
            onClick={() => void notificationsQuery.refetch()}
            disabled={notificationsQuery.isFetching}
            title="Refresh"
          >
            <i class={`fa-solid fa-rotate-right ${notificationsQuery.isFetching ? "spinning" : ""}`}></i>
          </button>
          <Show when={hasAnyNotifications()}>
            <button
              type="button"
              class="button-secondary"
              onClick={handleDismissAll}
              disabled={isClearing()}
              style="margin-right: 8px;"
            >
              Clear all
            </button>
          </Show>
        </div>
      </header>

      <Show when={!notificationsQuery.isLoading} fallback={<div class="placeholder-card">Loading notifications...</div>}>
        <Show when={hasAnyNotifications()} fallback={<div class="placeholder-card">No notifications yet.</div>}>
          {/* Horizontal tabs */}
          <div class="horizontal-tabs-container">
            <For each={NOTIFICATION_TYPES}>
              {(type) => (
                <div
                  class={`horizontal-tab-item ${activeTab() === type ? "active" : ""}`}
                  onClick={() => setActiveTab(type)}
                >
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <i class={typeIcons[type]} style="font-size: 0.875rem;"></i>
                    <span>{typeLabels[type]}</span>
                    <Show when={getTabCount(type) > 0}>
                      <span
                        style={{
                          background: "var(--text-muted)",
                          color: "white",
                          padding: "2px 8px",
                          "border-radius": "12px",
                          "font-size": "0.75rem",
                          "font-weight": "600"
                        }}
                      >
                        {getTabCount(type)}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDismissType();
                        }}
                        disabled={isClearing()}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: isClearing() ? "not-allowed" : "pointer",
                          padding: "2px",
                          display: "flex",
                          "align-items": "center",
                          opacity: isClearing() ? "0.5" : (activeTab() === type ? "1" : "0"),
                          visibility: activeTab() === type ? "visible" : "hidden",
                          width: "20px",
                          height: "20px"
                        }}
                        title={`Clear all ${typeLabels[type].toLowerCase()}`}
                      >
                        <i class="fas fa-times" style={{ color: "var(--danger)", "font-size": "16px" }}></i>
                      </button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>

          {/* Table for active tab */}
          <Show when={aggregatedNotifications().length > 0} fallback={<div class="placeholder-card">No {typeLabels[activeTab()].toLowerCase()} notifications.</div>}>
            <table class="chat-table">
              <thead>
                <tr>
                  <th style="width: 40px;"></th>
                  <th>Title</th>
                  <th class="mobile-hide">Message</th>
                  <th class="mobile-hide">Severity</th>
                  <th>Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <For each={aggregatedNotifications()}>
                  {(agg) => (
                    <>
                      {/* Aggregated row */}
                      <tr class={`notification-row notification-row--${agg.severity}`}>
                        <td>
                          <Show when={agg.count > 1}>
                            <button
                              type="button"
                              onClick={() => toggleGroupExpansion(agg.groupKey)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "4px",
                                display: "flex",
                                "align-items": "center"
                              }}
                              title={isGroupExpanded(agg.groupKey) ? "Collapse" : "Expand"}
                            >
                              <i
                                class={`fas fa-chevron-${isGroupExpanded(agg.groupKey) ? "down" : "right"}`}
                                style={{ color: "var(--text-muted)", "font-size": "14px" }}
                              ></i>
                            </button>
                          </Show>
                        </td>
                        <td class="notification-title">
                          <Show
                            when={agg.latest.link}
                            fallback={<span>{getAggregatedTitle(agg)}</span>}
                          >
                            <button
                              type="button"
                              class="link-button"
                              onClick={() => handleOpen(agg.latest)}
                              style={{
                                background: "none",
                                border: "none",
                                color: "var(--brand-aqua)",
                                cursor: "pointer",
                                "text-decoration": "underline",
                                padding: "0",
                                "text-align": "left"
                              }}
                            >
                              {getAggregatedTitle(agg)}
                            </button>
                          </Show>
                        </td>
                        <td class="chat-preview mobile-hide">{getAggregatedMessage(agg)}</td>
                        <td class="mobile-hide">
                          <span
                            class={`severity-badge severity-badge--${agg.severity}`}
                            style={{
                              padding: "0.25rem 0.5rem",
                              "border-radius": "4px",
                              "font-size": "0.875rem",
                              background:
                                agg.severity === "critical" ? "rgba(220, 38, 38, 0.1)" :
                                agg.severity === "warning" ? "rgba(245, 158, 11, 0.1)" :
                                agg.severity === "success" ? "rgba(16, 185, 129, 0.1)" :
                                "var(--surface-muted)",
                              color:
                                agg.severity === "critical" ? "var(--danger)" :
                                agg.severity === "warning" ? "var(--warning)" :
                                agg.severity === "success" ? "var(--success)" :
                                "var(--text-secondary)"
                            }}
                          >
                            {agg.severity}
                          </span>
                        </td>
                        <td class="chat-time"><TableDate date={agg.latest.createdAt} /></td>
                        <td style="text-align: center;">
                          <Show
                            when={agg.count === 1}
                            fallback={
                              <button
                                type="button"
                                onClick={() => handleDismissGroup(agg)}
                                disabled={isClearing()}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: isClearing() ? "not-allowed" : "pointer",
                                  padding: "4px",
                                  opacity: isClearing() ? "0.5" : "1",
                                  display: "flex",
                                  "flex-direction": "column",
                                  "align-items": "center",
                                  gap: "4px",
                                  margin: "0 auto"
                                }}
                                title={`Delete all ${agg.count} notifications`}
                              >
                                <i class="fas fa-times" style={{ color: "var(--danger)", "font-size": "24px" }}></i>
                                <span style={{ color: "var(--text-muted)", "font-size": "0.75rem" }}>
                                  {agg.count} items
                                </span>
                              </button>
                            }
                          >
                            <button
                              type="button"
                              onClick={() => handleDismissNotification(agg.latest.type, agg.latest.id)}
                              disabled={isClearing()}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: isClearing() ? "not-allowed" : "pointer",
                                padding: "4px",
                                opacity: isClearing() ? "0.5" : "1",
                                display: "flex",
                                "align-items": "center",
                                margin: "0 auto"
                              }}
                              title="Clear notification"
                            >
                              <i class="fas fa-times" style={{ color: "var(--danger)", "font-size": "24px" }}></i>
                            </button>
                          </Show>
                        </td>
                      </tr>

                      {/* Expanded individual notifications */}
                      <Show when={isGroupExpanded(agg.groupKey) && agg.count > 1}>
                        <For each={agg.notifications}>
                          {(item) => (
                            <tr
                              class={`notification-row notification-row--${item.severity}`}
                              style={{
                                "background-color": "var(--surface-muted)",
                                "border-left": "4px solid var(--brand-aqua)"
                              }}
                            >
                              <td style="padding-left: 2rem;"></td>
                              <td class="notification-title">
                                <Show
                                  when={item.link}
                                  fallback={<span>{item.title}</span>}
                                >
                                  <button
                                    type="button"
                                    class="link-button"
                                    onClick={() => handleOpen(item)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "var(--brand-aqua)",
                                      cursor: "pointer",
                                      "text-decoration": "underline",
                                      padding: "0",
                                      "text-align": "left"
                                    }}
                                  >
                                    {item.title}
                                  </button>
                                </Show>
                              </td>
                              <td class="chat-preview mobile-hide">{item.message}</td>
                              <td class="mobile-hide">
                                <span
                                  class={`severity-badge severity-badge--${item.severity}`}
                                  style={{
                                    padding: "0.25rem 0.5rem",
                                    "border-radius": "4px",
                                    "font-size": "0.875rem",
                                    background:
                                      item.severity === "critical" ? "rgba(220, 38, 38, 0.1)" :
                                      item.severity === "warning" ? "rgba(245, 158, 11, 0.1)" :
                                      item.severity === "success" ? "rgba(16, 185, 129, 0.1)" :
                                      "var(--surface-muted)",
                                    color:
                                      item.severity === "critical" ? "var(--danger)" :
                                      item.severity === "warning" ? "var(--warning)" :
                                      item.severity === "success" ? "var(--success)" :
                                      "var(--text-secondary)"
                                  }}
                                >
                                  {item.severity}
                                </span>
                              </td>
                              <td class="chat-time"><TableDate date={item.createdAt} /></td>
                              <td style="text-align: center;">
                                <button
                                  type="button"
                                  onClick={() => handleDismissNotification(item.type, item.id)}
                                  disabled={isClearing()}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: isClearing() ? "not-allowed" : "pointer",
                                    padding: "4px",
                                    opacity: isClearing() ? "0.5" : "1",
                                    display: "flex",
                                    "align-items": "center",
                                    margin: "0 auto"
                                  }}
                                  title="Clear notification"
                                >
                                  <i class="fas fa-times" style={{ color: "var(--danger)", "font-size": "24px" }}></i>
                                </button>
                              </td>
                            </tr>
                          )}
                        </For>
                      </Show>
                    </>
                  )}
                </For>
              </tbody>
            </table>
          </Show>
        </Show>
      </Show>
    </section>
  );
};